/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Cog, Megaphone, MegaphoneOff, Trash2, MessageSquare, Slack } from 'lucide-react';
import { useState } from 'react';
import { useSWRConfig } from 'swr';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogTrigger } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

import SubscriptionDialog from './SubscriptionDialog';

import { IWithPagination } from '@/commons/commons';
import { Subscription, SubscriptionStatus } from '@/commons/protocols';
import { useGet, useMutate } from '@/net/commons';
import { cn } from '@/utils';

type SubscriptionRowActionsProps = {
    subscription: Subscription;
    toggleDelete: () => void;
    toggleStatus: (status: SubscriptionStatus) => void;
};

const SubscriptionRowActions = ({ subscription, toggleDelete, toggleStatus }: SubscriptionRowActionsProps) => {
    const [open, setOpen] = useState(false);

    return (
        <AlertDialog>
            <Dialog open={open} onOpenChange={setOpen}>
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="flex h-7 w-7 p-0 data-[state=open]:bg-muted">
                            <DotsHorizontalIcon className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[160px]">
                        {subscription.status === 'enabled' && (
                            <DropdownMenuItem onSelect={() => toggleStatus('disabled')}>
                                <div className="flex w-full justify-between items-center">
                                    Disable
                                    <MegaphoneOff size={14} />
                                </div>
                            </DropdownMenuItem>
                        )}
                        {subscription.status === 'disabled' && (
                            <DropdownMenuItem onSelect={() => toggleStatus('enabled')}>
                                <div className="flex w-full justify-between items-center">
                                    Enable
                                    <Megaphone size={14} />
                                </div>
                            </DropdownMenuItem>
                        )}
                        <DialogTrigger asChild>
                            <DropdownMenuItem>
                                <div className="flex w-full justify-between items-center">
                                    Configure
                                    <Cog size={14} />
                                </div>
                            </DropdownMenuItem>
                        </DialogTrigger>
                        <AlertDialogTrigger asChild>
                            <DropdownMenuItem className="text-destructive">
                                <div className="flex w-full justify-between items-center">
                                    Delete
                                    <Trash2 size={14} />
                                </div>
                            </DropdownMenuItem>
                        </AlertDialogTrigger>
                    </DropdownMenuContent>
                </DropdownMenu>
                <SubscriptionDialog subscription={subscription} onClose={() => setOpen(false)} />
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove subscription</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to remove{' '}
                            <span className="font-semibold text-black">{subscription.name}</span>?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={toggleDelete}>
                            Yes, Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </Dialog>
        </AlertDialog>
    );
};

const SubscriptionStatusComp = ({ status, className }: { status: SubscriptionStatus; className?: string }) => {
    const statusStyles = {
        verification_pending: 'text-orange-500 border-orange-500 bg-orange-50',
        verification_failed: 'text-red-500 border-red-500 bg-red-50',
        enabled: 'text-green-500 border-green-500 bg-green-50',
        disabled: 'text-gray-500 border-gray-500 bg-gray-50',
    };

    const statusName = {
        verification_pending: 'verification pending',
        verification_failed: 'verification failed',
        enabled: 'enabled',
        disabled: 'disabled',
    };

    return (
        <span
            className={cn(
                'rounded-md border px-2.5 py-0.5 uppercase text-xs font-medium',
                statusStyles[status],
                className,
            )}
        >
            {statusName[status]}
        </span>
    );
};

const SubscriptionTypeComp = ({ type }: { type: 'slack' | 'webhook' }) => {
    return (
        <div className="flex items-center">
            {type === 'slack' ? (
                <Badge
                    variant="outline"
                    className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200 py-1"
                >
                    <Slack size={14} />
                    <span>Slack</span>
                </Badge>
            ) : (
                <Badge
                    variant="outline"
                    className="flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 py-1"
                >
                    <MessageSquare size={14} />
                    <span>Webhook</span>
                </Badge>
            )}
        </div>
    );
};

export default function SubscriptionsTable() {
    const { data: subscriptions } = useGet<IWithPagination<Subscription>>('/bff/organisation/subscriptions');
    const { mutate } = useSWRConfig();
    const { trigger: deleteSub } = useMutate('/bff/organisation/subscriptions/:id', {
        method: 'DELETE',
        onSuccess: () => {
            mutate('/bff/organisation/subscriptions');
        },
    });
    const { trigger: testSub } = useMutate('/bff/organisation/subscriptions/:id/test', {
        method: 'POST',
    });
    const { trigger: editSubStatus } = useMutate('/bff/organisation/subscriptions/:id', {
        method: 'PATCH',
        onSuccess: () => {
            mutate('/bff/organisation/subscriptions');
        },
    });

    const onDelete = async (id: number) => {
        await deleteSub({ idParam: id });
    };

    const onTest = async (id: number) => {
        await testSub({ idParam: id });
    };

    const onStatus = async (id: number, status: SubscriptionStatus) => {
        await editSubStatus({ idParam: id, status });
    };

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-[40%]">Name</TableHead>
                    <TableHead className="w-[20%]">Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[10%]" />
                </TableRow>
            </TableHeader>
            <TableBody>
                {subscriptions?.data.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                            There are no items to display.
                        </TableCell>
                    </TableRow>
                )}
                {subscriptions?.data.map((row) => (
                    <TableRow key={row.id}>
                        <TableCell>
                            <div className="flex items-center">
                                <span>{row.name}</span>
                                {row.type === 'slack' && row.slackChannelId && (
                                    <span className="ml-2 text-xs text-slate-500">({row.slackChannelId})</span>
                                )}
                            </div>
                        </TableCell>
                        <TableCell>
                            <SubscriptionTypeComp type={row.type} />
                        </TableCell>
                        <TableCell>
                            <SubscriptionStatusComp status={row.status} />
                        </TableCell>
                        <TableCell className="flex justify-end items-center">
                            {row.status === 'enabled' && (
                                <Button
                                    onClick={() => onTest(row.id)}
                                    variant="outline"
                                    className="ml-auto text-xs whitespace-nowrap h-6 px-2 mr-2"
                                >
                                    Send a test {row.type === 'slack' ? 'message' : 'webhook'}
                                </Button>
                            )}
                            <SubscriptionRowActions
                                subscription={row}
                                toggleDelete={() => onDelete(row.id)}
                                toggleStatus={(status: SubscriptionStatus) => onStatus(row.id, status)}
                            />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
